package com.merkost.handpass.admin.pages

import androidx.compose.runtime.*
import com.merkost.handpass.admin.components.*
import com.merkost.handpass.admin.container.UsersContainer
import com.merkost.handpass.admin.contract.*
import com.merkost.handpass.api.dto.admin.AdminUserListItem
import com.merkost.handpass.sharedweb.theme.*
import com.varabyte.kobweb.core.Page
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import com.merkost.handpass.admin.di.koinContainer
import pro.respawn.flowmvi.compose.dsl.subscribe

@Page("/users")
@Composable
fun UsersPage() {
    AdminPageScaffold(title = "User Management", currentPath = "/users", showTitle = false) {
        val container = koinContainer<UsersContainer>()
        with(container.store) {
            val state by subscribe { action ->
                when (action) {
                    is UsersAction.Navigate -> navigate(action.path)
                }
            }
            UsersContent(state, onIntent = ::intent)
        }
    }
}

@Composable
private fun AdminPageScope.UsersContent(state: UsersState, onIntent: (UsersIntent) -> Unit) {
    AdminPageHeader(
        title = "Users",
        subtitle = usersSubtitle(state),
        actions = {
            AdminSearchInput(
                initial = state.search,
                onDebouncedChange = { onIntent(UsersIntent.SetSearch(it)) },
                placeholder = "Search name or email…",
                widthPx = 280,
            )
            AdminButton(label = "Refresh", onClick = { onIntent(UsersIntent.Refresh) }, variant = AdminButtonVariant.Primary, size = AdminButtonSize.SM)
        },
    )

    Div({
        style {
            display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(HandpassSpacing.SM)
            property("flex-wrap", "wrap"); property("margin-bottom", "${HandpassSpacing.XL}")
        }
    }) {
        USER_STATUS_FILTERS.forEach { filter ->
            AdminButton(
                label = filter,
                onClick = { onIntent(UsersIntent.SetStatusFilter(filter)) },
                variant = if (state.statusFilter == filter) AdminButtonVariant.Primary else AdminButtonVariant.Secondary,
                size = AdminButtonSize.SM,
            )
        }
    }

    AdminListSection(
        title = "All users",
        count = state.totalCount.toInt(),
        data = state.data,
        emptyTitle = "No users found",
        skeletonRows = 8,
        onRetry = { onIntent(UsersIntent.Refresh) },
    ) { rows ->
        UsersTable(rows = rows, state = state, onIntent = onIntent)
    }

    Div({ style { property("margin-top", "${HandpassSpacing.XL}") } }) {
        AdminPagination(
            currentPage = state.page,
            totalPages = state.totalPages,
            totalCount = state.totalCount,
            onPageChange = { onIntent(UsersIntent.GoToPage(it)) },
        )
    }
}

private fun usersSubtitle(state: UsersState): String {
    val count = state.totalCount
    val noun = if (count == 1L) "user" else "users"
    return "$count $noun"
}

@Composable
private fun UsersTable(rows: List<AdminUserListItem>, state: UsersState, onIntent: (UsersIntent) -> Unit) {
    AdminTable(
        columns = listOf(
            AdminColumn<AdminUserListItem>("", "40px") {
                AdminAvatar(name = it.displayName, url = it.avatarUrl, size = AdminAvatarSize.SM)
            },
            AdminColumn("Name", "1fr", sortKey = "name") {
                Span({ style { fontWeight(600); fontSize(HandpassTypography.Body); color(OnSurface.value()) } }) {
                    Text(it.displayName)
                }
            },
            AdminColumn("Email", "1fr", sortKey = "email") {
                Span({ style { fontSize(HandpassTypography.Body); color(OnSurfaceMuted.value()) } }) {
                    Text(it.email)
                }
            },
            AdminColumn("Status", "120px") {
                AccountStatusBadge(it.accountStatus)
            },
            AdminColumn("Joined", "100px", sortKey = "joined") {
                Span({ style { fontSize(HandpassTypography.Caption); color(OnSurfaceMuted.value()) } }) {
                    Text(it.createdAt.take(10))
                }
            },
            AdminColumn("Listings", "80px") {
                Span({ style { fontSize(HandpassTypography.Body); color(OnSurface.value()) } }) {
                    Text(it.listingCount.toString())
                }
            },
            AdminColumn("Reports", "80px") {
                Span({
                    style {
                        fontSize(HandpassTypography.Body)
                        color(if (it.reportCount > 0) Danger.value() else OnSurface.value())
                    }
                }) {
                    Text(it.reportCount.toString())
                }
            },
        ),
        rows = rows,
        rowId = { it.id },
        onRowClick = { onIntent(UsersIntent.TapUser(it.id)) },
        sortBy = state.sortBy,
        sortAsc = state.sortAsc,
        onSort = { key ->
            val newAsc = if (state.sortBy == key) !state.sortAsc else true
            onIntent(UsersIntent.SetSort(key, newAsc))
        },
        flush = true,
    )
}
